import { useState, type ReactNode } from "react";
import { Link } from "react-router-dom";

import { EditPitchDialog } from "@/components/EditPitchDialog";
import { CreatorCell, CreatorCellSkeleton, PitchStatusBadge, PitchStatusBadgeSkeleton, RowActions, RowActionsSkeleton, TableShell, type TableColumn } from "@/components/tableKit";
import { Skeleton } from "@/components/ui/skeleton";
import { toast } from "@/components/ui/toast";
import { useSkeletonPhases } from "@/hooks/useSkeletonPhases";
import { analyticsService } from "../../../services/analytics";
import { resubmitPitch, withdrawPitch } from "../../../services/firebase";
import { formatMoney } from "../../../shared/currency";
import type { User } from "../../../shared/types";
import { timestampOrDateToDate } from "../../../utils/dates";
import { canEditPitch, canResubmitPitch, canWithdrawPitch } from "../../../utils/pitching/permissions";
import type { MyPitchRow } from "../rows";

type MyPitchColumnsOptions = {
    user: User | undefined;
    pendingId: string | null;
    onEdit: (row: MyPitchRow) => void;
    onStatusChange: (row: MyPitchRow, action: MyPitchStatusAction) => void;
};

function myPitchColumns({ user, pendingId, onEdit, onStatusChange }: MyPitchColumnsOptions): TableColumn<MyPitchRow>[] {
    return [
        {
            id: "creator",
            header: "Creator",
            cell: (row) => (
                <CreatorCell talent={row.talent ?? { id: row.talentId, name: row.talentName }} />
            ),
            skeleton: <CreatorCellSkeleton />,
        },
        {
            id: "brand",
            header: "Brand",
            cell: (row) => row.campaign?.booker?.company || "",
            skeleton: <Skeleton className="h-4 w-28" />,
        },
        {
            id: "campaign",
            header: "Campaign",
            skeleton: <Skeleton className="h-4 w-40" />,
            cell: (row) => row.campaign?.id ? (
                <Link to={`/campaign/${row.campaign.id}`} className="text-base/6 text-primary hover:underline">
                    {row.campaign.campaignName}
                </Link>
            ) : null,
        },
        {
            id: "status",
            header: "Status",
            cell: (row) => row.status ? <PitchStatusBadge status={row.status} /> : null,
            skeleton: <PitchStatusBadgeSkeleton />,
        },
        {
            id: "price",
            header: "Price",
            cell: (row) => formatMoney(row.price, row.campaign?.currency || "USD"),
            skeleton: <Skeleton className="h-4 w-16" />,
        },
        {
            id: "date",
            header: "Date",
            cell: (row) => pitchDateLabel(row),
            skeleton: <Skeleton className="h-4 w-24" />,
        },
        {
            id: "actions",
            header: "Actions",
            sticky: true,
            skeleton: <RowActionsSkeleton />,
            cell: (row) => (
                <RowActions
                    label="Pitch actions"
                    actions={[
                        ...(row.campaign && canEditPitch(user, row, row.talent)
                            ? [{ label: "Edit pitch", disabled: pendingId !== null, onSelect: () => onEdit(row) }]
                            : []),
                        ...(canWithdrawPitch(user, row, row.talent)
                            ? [{
                                label: "Withdraw",
                                destructive: true,
                                disabled: pendingId !== null,
                                onSelect: () => onStatusChange(row, "withdraw"),
                            }]
                            : []),
                        ...(canResubmitPitch(user, row, row.talent)
                            ? [{
                                label: "Resubmit Pitch",
                                disabled: pendingId !== null,
                                onSelect: () => onStatusChange(row, "resubmit"),
                            }]
                            : []),
                    ]}
                />
            ),
        },
    ];
}

function pitchDateLabel(row: MyPitchRow) {
    const created = row.docMeta?.createTime;
    if (!created) {
        return "";
    }
    return timestampOrDateToDate(created, true, "MMM D, YYYY") as string;
}

type MyPitchesTableProps = {
    rows: MyPitchRow[];
    user: User | undefined;
    isLoading?: boolean;
    empty?: ReactNode;
};

/** Manager pitches across campaigns. Edit stays available while the pitch is still open. */
export function MyPitchesTable({ rows, user, isLoading, empty }: MyPitchesTableProps) {
    const phase = useSkeletonPhases(Boolean(isLoading));
    const [editingId, setEditingId] = useState<string | null>(null);
    const [pendingId, setPendingId] = useState<string | null>(null);
    const editingRow = rows.find((row) => row.id === editingId);

    async function setPitchStatus(row: MyPitchRow, action: MyPitchStatusAction) {
        const isAllowed = action === "withdraw"
            ? canWithdrawPitch(user, row, row.talent)
            : canResubmitPitch(user, row, row.talent);
        if (!isAllowed || pendingId !== null) {
            return;
        }
        setPendingId(row.id);
        try {
            const result = action === "withdraw"
                ? await withdrawPitch(row.id)
                : await resubmitPitch(row.id);
            if (!result.success) {
                console.error(result.error);
                toast.add({
                    type: "error",
                    title: action === "withdraw" ? "Couldn’t withdraw this pitch" : "Couldn’t resubmit this pitch",
                    description: "Refresh and try again.",
                });
            }
        } catch (error) {
            console.error(error);
            analyticsService().logError(error);
            toast.add({
                type: "error",
                title: "Couldn’t update this pitch",
                description: "Refresh and try again.",
            });
        } finally {
            setPendingId(null);
        }
    }

    const columns = myPitchColumns({
        user,
        pendingId,
        onEdit: (row) => setEditingId(row.id),
        onStatusChange: (row, action) => {
            void setPitchStatus(row, action);
        },
    });

    return (
        <>
            {phase === "showNothing" ? null : (
                <TableShell
                    columns={columns}
                    rows={rows}
                    getRowId={(row) => row.id}
                    isLoading={phase === "showSkeleton"}
                    empty={empty}
                />
            )}
            <EditPitchDialog
                pitch={editingRow ?? null}
                campaign={editingRow?.campaign}
                user={user}
                onOpenChange={(open) => {
                    if (!open) {
                        setEditingId(null);
                    }
                }}
            />
        </>
    );
}

type MyPitchStatusAction = "withdraw" | "resubmit";
